// FILE: PullRequestDetailPanel.tsx
// Purpose: One pull request, opened beside the list — summary, timeline and code, plus the
//          actions and the comment box.
// Layer: Component
// Exports: PullRequestDetailPanel
//
// Three tabs and three reads, deliberately not one. The summary is a single cheap call; the
// timeline pages far deeper and would otherwise hold the title and the merge button off screen;
// the patch is megabytes and is the reason the code tab is lazy in every sense — its module, its
// query and its worker all arrive the first time somebody opens it, and never before.

import type { PullRequestListEntry, PullRequestRef } from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useState } from "react";

import { DiffPanelLoadingState, DiffPanelShell } from "~/components/DiffPanelShell";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { Toggle, ToggleGroup } from "~/components/ui/toggle-group";
import { ExternalLinkIcon, XIcon } from "~/lib/icons";
import {
  pullRequestsActivityQueryOptions,
  pullRequestsDetailQueryOptions,
} from "~/lib/pullRequestsReactQuery";
import { cn } from "~/lib/utils";
import { PullRequestActions } from "./PullRequestActions";
import { PullRequestFailure } from "./PullRequestFailure";
import { PullRequestSummaryTab } from "./PullRequestSummaryTab";
import { PullRequestTimelineTab } from "./PullRequestTimelineTab";
import { pullRequestStateDisplay } from "./pullRequestPresentation";

const PullRequestCodeTab = lazy(() => import("./PullRequestCodeTab"));

const TABS = ["summary", "timeline", "code"] as const;
export type PullRequestPanelTab = (typeof TABS)[number];

const TAB_LABELS: Record<PullRequestPanelTab, string> = {
  summary: "Summary",
  timeline: "Timeline",
  code: "Code",
};

export function PullRequestDetailPanel({
  entry,
  onClose,
  className,
}: {
  /** The row that was clicked. Its fields draw the header immediately, so the panel has a title
   * and a state glyph before the detail call has answered — the list already knows both. */
  entry: PullRequestListEntry;
  onClose: () => void;
  className?: string;
}) {
  const [tab, setTab] = useState<PullRequestPanelTab>("summary");
  const reference: PullRequestRef = { projectId: entry.projectId, number: entry.number };

  const detailQuery = useQuery(pullRequestsDetailQueryOptions(reference));
  // Asked for only while its tab is open: a pull request with hundreds of remarks costs a page of
  // reads, and most visits to this panel never leave the summary.
  const activityQuery = useQuery({
    ...pullRequestsActivityQueryOptions(reference),
    enabled: tab === "timeline",
  });

  const headerState = pullRequestStateDisplay(detailQuery.data ?? entry);

  return (
    <DiffPanelShell
      mode="inline"
      header={
        <>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <headerState.Icon
              className={cn("size-4 shrink-0", headerState.colorClass)}
              aria-hidden
            />
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              #{entry.number}
            </span>
            <span className="truncate text-sm font-medium text-foreground">
              {detailQuery.data?.title ?? entry.title}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {/* Children stay on the Button and the rendered element is childless — the shape
                every other `render` in this codebase uses, because base-ui merges them in. */}
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={`Open #${entry.number} on GitHub`}
              render={<a href={entry.url} target="_blank" rel="noreferrer noopener" />}
            >
              <ExternalLinkIcon className="size-3.5" />
            </Button>
            <Button size="icon-xs" variant="ghost" onClick={onClose} aria-label="Close panel">
              <XIcon />
            </Button>
          </div>
        </>
      }
    >
      <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
        <div className="shrink-0 border-b border-border/60 px-3 py-2">
          <ToggleGroup
            variant="outline"
            size="xs"
            value={[tab]}
            onValueChange={(value) => {
              const next = value[0];
              // An empty array is the reader pressing the tab they are already on. Staying put is
              // the right answer; a panel with no tab selected is not a state that exists.
              if (next && (TABS as ReadonlyArray<string>).includes(next)) {
                setTab(next as PullRequestPanelTab);
              }
            }}
          >
            {TABS.map((value) => (
              <Toggle key={value} value={value} aria-label={TAB_LABELS[value]}>
                {TAB_LABELS[value]}
              </Toggle>
            ))}
          </ToggleGroup>
        </div>

        {tab === "summary" ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {detailQuery.isLoading ? (
                <div className="space-y-2 p-3">
                  <Skeleton className="h-4 w-2/3 rounded" />
                  <Skeleton className="h-4 w-1/2 rounded" />
                  <Skeleton className="h-24 w-full rounded-xl" />
                </div>
              ) : detailQuery.isError ? (
                <div className="p-3">
                  <PullRequestFailure
                    error={detailQuery.error}
                    fallbackTitle="Could not load this pull request"
                    onRetry={() => void detailQuery.refetch()}
                  />
                </div>
              ) : detailQuery.data ? (
                <PullRequestSummaryTab detail={detailQuery.data} />
              ) : null}
            </div>
            {detailQuery.data ? (
              <PullRequestActions reference={reference} detail={detailQuery.data} />
            ) : null}
          </>
        ) : null}

        {tab === "timeline" ? (
          <PullRequestTimelineTab
            reference={reference}
            activity={activityQuery.data}
            isLoading={activityQuery.isLoading}
            error={activityQuery.isError ? activityQuery.error : null}
            onRetry={() => void activityQuery.refetch()}
          />
        ) : null}

        {tab === "code" ? (
          <Suspense fallback={<DiffPanelLoadingState label="Loading the diff viewer" />}>
            <PullRequestCodeTab reference={reference} />
          </Suspense>
        ) : null}
      </div>
    </DiffPanelShell>
  );
}
